import { Link } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { Box, IconButton, Typography } from '@mui/material';
import { FiMessageSquare } from 'react-icons/fi';
import {
  MdInfoOutline,
  MdOutlineAttachFile,
  MdOutlineCall,
  MdOutlineDesktopWindows,
  MdOutlineRemoveRedEye
} from 'react-icons/md';
import { Signal } from '@/apis/business/agent/signals/types';
import ReportFlagIcon from '@/assets/icons/ReportFlagIcon';
import { formatDate, getWebChannelSourceLabel, titleCase } from 'utils/helperFunctions';
import Tooltip from '@/components/Common/Tooltip';
import { getSentimentColor, getSeverityBadges, getColumnBadges, normalizeSingle } from './helpers';

export const getSignalsColumns = (theme: any, search = ''): ColumnDef<Signal>[] => [
  {
    header: 'SOURCE',
    cell: (info) => {
      const { channel, contact_info, created_at, report_call: report } = info?.row?.original || {};
      const getIconLabel = (sourceType: string) => {
        switch (sourceType) {
          case 'personalized_link_web':
          case 'personalized_link_chat':
            return { icon: <MdOutlineAttachFile />, label: 'Personalized Link' };
          case 'phone_call':
            return { icon: <MdOutlineCall />, label: contact_info.phone_number || '-' };
          case 'phone_chat':
            return { icon: <FiMessageSquare />, label: contact_info.phone_number || '-' };
          default:
            return {
              icon: <MdOutlineDesktopWindows />,
              label: getWebChannelSourceLabel(contact_info?.phone_number, created_at)
            };
        }
      };
      const { icon, label } = getIconLabel(channel);
      return (
        <Box display='flex' gap={1} alignItems='center' fontWeight={500} sx={{ minWidth: 0 }}>
          <Box mt='3px' sx={{ flexShrink: 0 }}>
            {icon}
          </Box>
          <Typography>{label}</Typography>
          {report?.description && (
            <Tooltip title={report.description} placement='top'>
              <Box display='flex' sx={{ flexShrink: 0, lineHeight: 0 }}>
                <ReportFlagIcon />
              </Box>
            </Tooltip>
          )}
        </Box>
      );
    }
  },
  {
    header: 'Date',
    accessorKey: 'created_at',
    cell: (info) => (
      <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {formatDate(info.getValue<string>(), 'MMM D, YYYY')}
      </Box>
    )
  },
  {
    header: 'Summary',
    accessorKey: 'summary',
    cell: (info) => {
      const rawValue = info.getValue<Signal['summary']>();
      const value = typeof rawValue === 'string' ? rawValue : '';
      const isTruncated = value.length > 10;
      const truncated = isTruncated ? `${value.slice(0, 10)}...` : value || '-';
      return (
        <Box display='flex' flexDirection='row' alignItems='center'>
          <Typography noWrap>{truncated}</Typography>
          {isTruncated && (
            <Tooltip title={value}>
              <MdInfoOutline size='20px' color={theme.palette.primary.main} />
            </Tooltip>
          )}
        </Box>
      );
    }
  },
  {
    header: 'Persona',
    accessorKey: 'persona',
    cell: (info) => {
      const value = normalizeSingle(info.getValue<string | string[]>());
      return <Typography>{titleCase(value) || '-'}</Typography>;
    }
  },
  {
    header: () => 'Sentiment',
    accessorKey: 'sentiment',
    cell: (info) => {
      const value = normalizeSingle(info.getValue<string | string[]>());
      return <Typography color={getSentimentColor(value)}>{value}</Typography>;
    }
  },
  {
    header: () => 'Severity',
    accessorKey: 'severity',
    cell: (info) => {
      const value = normalizeSingle(info.getValue<string | string[]>());
      return value === 'Neutral' ? <Typography textAlign='center'>__</Typography> : getSeverityBadges(value);
    }
  },
  {
    header: 'Keywords',
    accessorKey: 'keywords',
    cell: (info) => getColumnBadges(info.row.original.keywords ?? [], '#D2ECFF', '#195EFF')
  },
  {
    header: 'Workflows',
    accessorKey: 'workflows',
    cell: (info) => getColumnBadges(info?.row?.original?.workflows ?? [], '#D2ECFF', '#195EFF')
  },
  {
    header: 'Action',
    cell: (info) => (
      <Box display='flex' gap={0.5} alignItems='center'>
        <Box fontSize={18} borderRadius='50%' bgcolor='#D2ECFF'>
          <Link to={{ pathname: String(info.row.original.interaction_id), search }}>
            <IconButton>
              <MdOutlineRemoveRedEye color='#195EFF' size={18} />
            </IconButton>
          </Link>
        </Box>
      </Box>
    )
  }
];
